/**
 * Complete Parametric SVG Character with forward kinematics, lip sync, expression controller,
 * customizable skin/hair/outfit colors, accessories, and character-wise voice selection.
 */
import { SVGObject, SVGObjectConfig } from '../objects/SVGObject';
import { CharacterRig, RigColors } from './CharacterRig';
import { CharacterAnimator } from './CharacterAnimator';
import { ExpressionController, EmotionType } from './ExpressionController';
import { CharacterPresets, CharacterVoiceConfig } from './CharacterPresets';
import { LipSync, VisemeType } from './LipSync';

export interface CharacterProps {
  preset?: string;
  name?: string;
  voice?: CharacterVoiceConfig;
  colors?: Partial<RigColors>;
  geometry?: any;
  accessories?: {
    glasses?: boolean;
    labCoat?: boolean;
    crown?: boolean;
    tie?: boolean;
    cap?: boolean;
    [key: string]: any;
  };
  emotion?: EmotionType;
  flipX?: boolean;
}

export type CharacterActionState = 'idle' | 'walking' | 'waving' | 'pointing' | 'writing' | 'clapping' | 'talking';

export class Character extends SVGObject {
  public props: CharacterProps;
  public rig: CharacterRig;
  public animator: CharacterAnimator;
  public expressions: ExpressionController;

  private actionState: CharacterActionState = 'idle';
  private flipX: boolean = false;

  // Lip-sync timing
  private visemeQueue: { time: number; viseme: VisemeType }[] = [];
  private speechElapsed: number = 0;
  private isSpeaking: boolean = false;

  constructor(config: SVGObjectConfig & { props?: CharacterProps }) {
    super({ ...config, type: 'character', layer: 'characters' });
    this.props = {
      ...config.props
    };

    let colors = this.props.colors;
    let accessories = this.props.accessories;
    let geometry = this.props.geometry;

    if (this.props.preset && (CharacterPresets as any)[this.props.preset]) {
      const p = (CharacterPresets as any)[this.props.preset];
      colors = { ...p.colors, ...colors };
      accessories = { ...p.accessories, ...accessories };
      geometry = { ...p.geometry, ...geometry };
      if (!this.props.voice) {
        this.props.voice = { ...p.voice };
      }
      if (config.scaleX === undefined && config.scaleY === undefined && p.scale) {
        this.scaleX = p.scale;
        this.scaleY = p.scale;
      }
    }

    this.props.colors = colors;
    this.props.accessories = accessories;
    this.props.geometry = geometry;
    if (!this.props.voice) {
      this.props.voice = { gender: 'male', pitch: 1.0, rate: 1.0 };
    }

    this.flipX = this.props.flipX ?? false;
    this.rig = new CharacterRig(colors, accessories, geometry);
    this.animator = new CharacterAnimator(this.rig);
    this.expressions = new ExpressionController();

    if (this.props.emotion) {
      this.expressions.setEmotion(this.props.emotion);
    }
  }

  public rebuildRig(): void {
    const currentEmotion = this.expressions.getEmotion();
    this.rig = new CharacterRig(this.props.colors, this.props.accessories, this.props.geometry);
    this.animator = new CharacterAnimator(this.rig);
    this.expressions.setEmotion(currentEmotion);
    this.render();
  }

  public render(): void {
    this.container.innerHTML = '';
    this.container.appendChild(this.rig.root);
    this.updateFacing();
  }

  public override update(time: number, delta: number): void {
    super.update(time, delta);

    // 1. Process Actions
    switch (this.actionState) {
      case 'walking':
        this.animator.animateWalk(1.2, delta);
        break;
      case 'waving':
        this.animator.animateWave(delta);
        break;
      case 'writing':
        this.animator.animateWrite(delta);
        break;
      case 'clapping':
        this.animator.animateClap(delta);
        break;
      case 'pointing':
        this.animator.animatePoint(0, 0);
        break;
      case 'talking':
      case 'idle':
      default:
        this.animator.updateIdle(delta);
        break;
    }

    // 2. Process Lip-sync
    if (this.isSpeaking && this.visemeQueue.length > 0) {
      this.speechElapsed += delta;
      let activeViseme: VisemeType = 'rest';

      for (let i = 0; i < this.visemeQueue.length; i++) {
        if (this.speechElapsed >= this.visemeQueue[i].time) {
          activeViseme = this.visemeQueue[i].viseme;
        }
      }

      this.rig.setViseme(activeViseme);

      const last = this.visemeQueue[this.visemeQueue.length - 1];
      if (this.speechElapsed >= last.time + 0.2) {
        if (this.actionState === 'talking') {
          this.speechElapsed = 0;
        } else {
          this.isSpeaking = false;
          this.rig.setViseme('rest');
        }
      }
    } else if (this.actionState === 'talking') {
      const cycleTime = (time % 0.35) / 0.35;
      const visemes: VisemeType[] = ['A', 'E', 'LNTD', 'O', 'smile'];
      const vIdx = Math.floor(cycleTime * visemes.length);
      this.rig.setViseme(visemes[vIdx]);
    } else {
      this.rig.setViseme('rest');
    }

    // 3. Apply Expressions
    this.rig.applyFacialState(this.expressions.getState());
  }

  public setEmotion(emotion: EmotionType): void {
    this.props.emotion = emotion;
    this.expressions.setEmotion(emotion);
  }

  public setAction(action: CharacterActionState): void {
    this.actionState = action;
  }

  public setFacing(flip: boolean): void {
    this.flipX = flip;
    this.props.flipX = flip;
    this.updateFacing();
  }

  private updateFacing(): void {
    const sX = (this.flipX ? -1 : 1) * Math.abs(this.scaleX);
    this.scaleX = sX;
  }

  public speakText(text: string, duration: number = 3.0): void {
    this.visemeQueue = LipSync.textToVisemes(text, duration);
    this.speechElapsed = 0;
    this.isSpeaking = true;
  }
}
